import {
  contact,
  entries,
  heroLoop,
  profile,
  sections,
} from './content.js';

const app = document.getElementById('app');

if (!app) {
  throw new Error('App root not found.');
}

const visibleSections = sections.filter((section) => section.id !== 'writing');
const validSectionIds = new Set(visibleSections.map((section) => section.id));
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const desktopMedia = window.matchMedia('(min-width: 901px)');
const railItems = [
  {
    type: 'section',
    id: 'about',
    label: 'ABOUT',
    tone: 'amber',
  },
  {
    type: 'section',
    id: 'projects',
    label: 'PROJECTS',
    tone: 'amber',
  },
  {
    type: 'external',
    label: 'LINKEDIN',
    href: contact.linkedin.url,
    tone: 'amber',
  },
];

const state = {
  activeSection: resolveSectionFromHash(),
  activeProjectId: entries[0]?.id ?? null,
};
const MAX_PROJECT_MEDIA = 2;
let projectMediaSwapToken = 0;

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

app.innerHTML = `
  <div class="page-shell">
    <div class="background-stack" aria-hidden="true">
      <img class="background-poster" src="${heroLoop.poster}" alt="">
      <video
        class="background-video"
        src="${heroLoop.src}"
        poster="${heroLoop.poster}"
        width="1600"
        height="900"
        autoplay
        muted
        loop
        playsinline
        preload="metadata"
      ></video>
    </div>

    <div class="page-overlay">
      <main class="experience-shell">
        <aside class="rail" aria-label="Profile navigation">
          <a class="brand-lockup" href="/" data-home>
            <span class="brand-name">${profile.name}</span>
          </a>

          <nav class="rail-nav" aria-label="Primary sections"></nav>
        </aside>

        <section class="stage is-empty" id="stage-content" aria-live="polite"></section>
      </main>
    </div>
  </div>
`;

const navEl = app.querySelector('.rail-nav');
const stageEl = app.querySelector('#stage-content');

if (!navEl || !stageEl) {
  throw new Error('Integrated shell failed to initialize.');
}

function resolveSectionFromHash() {
  const requested = window.location.hash.replace('#', '').trim();

  if (!requested) {
    return null;
  }

  if (validSectionIds.has(requested)) {
    return requested;
  }

  const url = new URL(window.location.href);
  url.hash = '';
  history.replaceState(null, '', url);
  return null;
}

function setSection(sectionId) {
  const nextSection = validSectionIds.has(sectionId) ? sectionId : null;
  const url = new URL(window.location.href);

  if (nextSection) {
    url.hash = nextSection;
  } else {
    url.hash = '';
  }

  history.pushState(null, '', url);
  state.activeSection = nextSection;

  if (desktopMedia.matches) {
    stageEl.scrollTop = 0;
  }

  render();
}

function getActiveProject() {
  return entries.find((entry) => entry.id === state.activeProjectId) ?? entries[0] ?? null;
}

function renderRailNav() {
  navEl.innerHTML = railItems
    .map(
      (item, index) => {
        const railIndex = String(index + 1).padStart(2, '0');

        if (item.type === 'external') {
          return `
            <a
              class="rail-link rail-link--external"
              href="${item.href}"
              target="_blank"
              rel="noreferrer noopener"
              data-tone="${item.tone}"
            >
              <span class="rail-link__index">${railIndex}</span>
              <span class="rail-link__body">
                <span class="rail-link__label">${item.label}</span>
              </span>
            </a>
          `;
        }

        return `
          <a
            class="rail-link${item.id === state.activeSection ? ' is-active' : ''}"
            href="#${item.id}"
            data-section="${item.id}"
            data-tone="${item.tone}"
            ${item.id === state.activeSection ? 'aria-current="page"' : ''}
          >
            <span class="rail-link__index">${railIndex}</span>
            <span class="rail-link__body">
              <span class="rail-link__label">${item.label}</span>
            </span>
          </a>
        `;
      }
    )
    .join('');
}

function renderLinkItems(links) {
  return links
    .map(
      (link) => `
        <a class="inline-link" href="${link.url}" target="_blank" rel="noreferrer noopener">
          ${link.label}
        </a>
      `
    )
    .join('');
}

function renderLinks(links) {
  if (!links.length) {
    return '';
  }

  return `<div class="inline-links">${renderLinkItems(links)}</div>`;
}

function getPreviewStyle(preview) {
  return `aspect-ratio: ${preview.aspectRatio ?? '16 / 10'}; object-fit: ${preview.fit ?? 'cover'}; object-position: ${preview.position ?? 'center center'};`;
}

function renderAboutSection() {
  return `
    <article class="stage-panel stage-panel--about" aria-labelledby="about-title">
      <div class="about-layout">
        <header class="about-intro">
          <p class="panel-kicker">About</p>
          <h1 class="panel-title panel-title--about" id="about-title">${profile.intro}</h1>
        </header>

        <div class="about-sections">
          ${profile.aboutSections
            .map(
              (section) => `
                <section class="about-block">
                  <p class="about-block__label">${section.label}</p>
                  <p class="about-block__value">${section.value}</p>
                </section>
              `
            )
            .join('')}
        </div>
      </div>
    </article>
  `;
}

function renderProjectPicker(activeProject) {
  return `
    <div class="project-switcher" aria-label="Project selection">
      ${entries
        .map(
          (entry, index) => `
            <button
              class="project-switch${entry.id === activeProject.id ? ' is-active' : ''}"
              type="button"
              data-project="${entry.id}"
              data-tone="${entry.tone ?? 'dusk'}"
              aria-pressed="${entry.id === activeProject.id}"
            >
              <span class="project-switch__index">${String(index + 1).padStart(2, '0')}</span>
              <span class="project-switch__body">
                <span class="project-switch__title">${entry.title.toUpperCase()}</span>
              </span>
            </button>
          `
        )
        .join('')}
    </div>
  `;
}

function renderProjectVisual(entry) {
  const media = entry.media.slice(0, MAX_PROJECT_MEDIA);

  return `
    <figure class="project-visual${media.length > 1 ? ' project-visual--stack' : ''}" data-project-visual>
      ${Array.from({ length: MAX_PROJECT_MEDIA }, (_, index) => {
        const preview = media[index];

        if (!preview) {
          return `
            <img
              data-project-image="${index}"
              alt=""
              hidden
              loading="${index === 0 ? 'eager' : 'lazy'}"
              decoding="async"
            >
          `;
        }

        return `
          <img
            data-project-image="${index}"
            src="${preview.src}"
            alt="${preview.alt}"
            loading="${index === 0 ? 'eager' : 'lazy'}"
            decoding="async"
            style="${getPreviewStyle(preview)}"
          >
        `;
      }).join('')}
      <figcaption data-project-caption>${entry.meta}</figcaption>
    </figure>
  `;
}

function renderProjectBody(activeProject) {
  return `
    <div class="project-copy">
      <p class="project-kicker" data-project-kicker>${activeProject.meta}</p>
      <h1 class="panel-title panel-title--project" id="projects-title" data-project-title>${activeProject.title}</h1>
      <p class="panel-summary panel-summary--project" data-project-summary>${activeProject.summary}</p>

      <div class="tag-row" data-project-tags>
        ${activeProject.tags.map((tag) => `<span class="tag-chip">${tag}</span>`).join('')}
      </div>

      <div class="panel-copy" data-project-detail>
        ${activeProject.detail.map((paragraph) => `<p>${paragraph}</p>`).join('')}
      </div>

      <div data-project-links>
        ${renderLinks(activeProject.links)}
      </div>
    </div>

    ${renderProjectVisual(activeProject)}
  `;
}

function renderProjectsSection() {
  const activeProject = getActiveProject();

  if (!activeProject) {
    return '';
  }

  return `
    <article class="stage-panel stage-panel--projects" aria-labelledby="projects-title">
      <div class="project-layout">
        <aside class="project-list" aria-label="Projects">
          <p class="panel-kicker">Projects</p>
          ${renderProjectPicker(activeProject)}
        </aside>

        <div class="project-content">${renderProjectBody(activeProject)}</div>
      </div>
    </article>
  `;
}

function renderSection() {
  switch (state.activeSection) {
    case 'about':
      return renderAboutSection();
    case 'projects':
      return renderProjectsSection();
    default:
      return '';
  }
}

function animateStage() {
  if (prefersReducedMotion.matches || !stageEl.animate || !state.activeSection) {
    return;
  }

  stageEl.animate(
    [
      {
        opacity: 0,
        transform: 'translateY(18px) scale(0.985)',
        filter: 'blur(12px)',
      },
      {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
        filter: 'blur(0)',
      },
    ],
    {
      duration: 360,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    }
  );
}

function preloadProjectMedia(media) {
  return Promise.all(
    media.map(
      (preview) =>
        new Promise((resolve) => {
          const image = new Image();

          image.decoding = 'async';
          image.onload = () => resolve();
          image.onerror = () => resolve();
          image.src = preview.src;
        })
    )
  );
}

function applyProjectTextContent(projectContentEl, activeProject) {
  const kickerEl = projectContentEl.querySelector('[data-project-kicker]');
  const titleEl = projectContentEl.querySelector('[data-project-title]');
  const summaryEl = projectContentEl.querySelector('[data-project-summary]');
  const tagsEl = projectContentEl.querySelector('[data-project-tags]');
  const detailEl = projectContentEl.querySelector('[data-project-detail]');
  const linksEl = projectContentEl.querySelector('[data-project-links]');
  const captionEl = projectContentEl.querySelector('[data-project-caption]');

  if (kickerEl) kickerEl.textContent = activeProject.meta;
  if (titleEl) titleEl.textContent = activeProject.title;
  if (summaryEl) summaryEl.textContent = activeProject.summary;
  if (tagsEl) {
    tagsEl.innerHTML = activeProject.tags.map((tag) => `<span class="tag-chip">${tag}</span>`).join('');
  }
  if (detailEl) {
    detailEl.innerHTML = activeProject.detail.map((paragraph) => `<p>${paragraph}</p>`).join('');
  }
  if (linksEl) {
    linksEl.innerHTML = renderLinks(activeProject.links);
  }
  if (captionEl) {
    captionEl.textContent = activeProject.meta;
  }
}

function applyProjectMediaContent(projectContentEl, activeProject) {
  const projectVisualEl = projectContentEl.querySelector('[data-project-visual]');

  if (!projectVisualEl) {
    return;
  }

  const media = activeProject.media.slice(0, MAX_PROJECT_MEDIA);
  projectVisualEl.classList.toggle('project-visual--stack', media.length > 1);

  for (let index = 0; index < MAX_PROJECT_MEDIA; index += 1) {
    const imageEl = projectVisualEl.querySelector(`[data-project-image="${index}"]`);
    const preview = media[index];

    if (!imageEl) {
      continue;
    }

    if (!preview) {
      imageEl.hidden = true;
      imageEl.removeAttribute('src');
      imageEl.alt = '';
      imageEl.removeAttribute('style');
      continue;
    }

    imageEl.hidden = false;
    imageEl.src = preview.src;
    imageEl.alt = preview.alt;
    imageEl.style.cssText = getPreviewStyle(preview);
  }
}

function render() {
  renderRailNav();
  stageEl.innerHTML = renderSection();
  stageEl.classList.toggle('is-empty', !state.activeSection);
  stageEl.classList.toggle('is-active', Boolean(state.activeSection));

  const activeMeta = visibleSections.find((section) => section.id === state.activeSection);
  document.title = activeMeta ? `${profile.name} / ${activeMeta.label}` : profile.name;

  animateStage();
}

function updateProjectsSection() {
  const activeProject = getActiveProject();

  if (state.activeSection !== 'projects' || !activeProject) {
    render();
    return;
  }

  const projectContentEl = stageEl.querySelector('.project-content');

  if (!projectContentEl) {
    render();
    return;
  }

  applyProjectTextContent(projectContentEl, activeProject);

  stageEl.querySelectorAll('[data-project]').forEach((projectButton) => {
    const isActive = projectButton.getAttribute('data-project') === activeProject.id;
    projectButton.classList.toggle('is-active', isActive);
    projectButton.setAttribute('aria-pressed', String(isActive));
  });

  const swapToken = ++projectMediaSwapToken;
  preloadProjectMedia(activeProject.media.slice(0, MAX_PROJECT_MEDIA)).then(() => {
    if (swapToken !== projectMediaSwapToken) {
      return;
    }

    applyProjectMediaContent(projectContentEl, activeProject);
  });
}

window.addEventListener('popstate', () => {
  state.activeSection = resolveSectionFromHash();
  render();
});

app.addEventListener('click', (event) => {
  const homeTrigger = event.target.closest('[data-home]');

  if (homeTrigger) {
    event.preventDefault();

    if (state.activeSection === null) {
      return;
    }

    setSection(null);
    return;
  }

  const sectionTrigger = event.target.closest('[data-section]');

  if (sectionTrigger) {
    const requestedSection = sectionTrigger.getAttribute('data-section');
    const nextSection = requestedSection && validSectionIds.has(requestedSection)
      ? requestedSection
      : null;

    if (nextSection === state.activeSection) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    setSection(nextSection);
    return;
  }

  const projectTrigger = event.target.closest('[data-project]');

  if (!projectTrigger) {
    return;
  }

  const projectId = projectTrigger.getAttribute('data-project');

  if (!projectId || projectId === state.activeProjectId) {
    return;
  }

  state.activeProjectId = projectId;
  updateProjectsSection();
});

render();
